import React, { useState } from 'react'
import { makeStyles, Theme } from '@material-ui/core/styles'
import { Button, Divider, Grid, Link } from '@material-ui/core'
import { FormattedMessage, FormattedNumber } from 'react-intl'
import ShareNewIcon from '@src/icons/ShareNewIcon'
import { useCurrentUser } from '@src/utils/useCurrentUser'
import { useFormikContext } from 'formik'
import { MilliplexFormValues } from '../Milliplex'
import { SelectedKits } from '@src/routes/Configurators/products/Milliplex/utils'
import QuoteRequestToDealerModal from './QuoteRequestToDealerModal'
import ShareLinkDropDown from './ShareLinkDropdown'
import AddToCartItems from '@src/routes/Configurators/components/AddToCart/AddToCartItems'
import { useAddToCartMilliplexMutation } from '@src/mutations/MilliplexAddToCartMutation.generated'
import { useUserSession } from '@utils/useUserSession'
import { ProductEnum } from '../enum'
import CircleLoader from '@src/components/CircleLoader'
import {
  ConfiguratorEventProduct,
  sendAddToCartConfiguratorEvent,
} from '@src/utils/analytics/configuratorAnalytics'
import {
  priceFormatter,
  regexForStripEncodingUnicode,
  regexForStripHTML,
  regexForStripSymbols,
} from '@src/utils/analytics/coreAnalytics'

const useStyles = makeStyles((theme: Theme) => ({
  containerWrapper: {
    margin: theme.spacing(6, 0, 0),
    display: 'flex',
    justifyContent: 'flex-end',
  },
  content: {
    width: '38%',
    display: 'flex',
    justifyContent: 'flex-start',
    flexDirection: 'column',
  },
  heading: {
    fontSize: theme.typography.pxToRem(16),
    fontWeight: theme.typography.fontWeightBold,
  },
  divider: {
    backgroundColor: theme.palette.grey['500'],
    height: 1,
    margin: theme.spacing(2.75, 0),
    width: '100%',
  },
  noteInfo: {
    fontSize: theme.typography.pxToRem(12),
    paddingRight: theme.spacing(7.5),
  },
  noteText: {
    fontWeight: theme.typography.fontWeightBold,
  },
  addToCart: {
    margin: theme.spacing(6, 0),
  },
  shareIcon: {
    color: theme.palette.primary.main,
    fontSize: theme.typography.pxToRem(24),
    alignSelf: 'center',
    marginRight: theme.spacing(2),
    cursor: 'pointer',
    '& g:hover': {
      stroke: theme.palette.primary.dark,
    },
  },
  shareDiv: {
    lineHeight: 1,
  },
  shareText: {
    position: 'relative',
    bottom: '15%',
    cursor: 'pointer',
  },
  shareLink: {
    fontSize: theme.typography.pxToRem(16),
    fontWeight: 900,
    color: theme.palette.primary.main,
  },
}))

interface MilliplexProductInput {
  sapNextEnabled: boolean
  timeZone: string
  store: string
}

const SubtotalAndAddToCart = () => {
  const classes = useStyles()
  const [sendRequestToDealerModal, setSendRequestToDealerModal] =
    useState(false)
  const [shareLinkClicked, setShareLinkClicked] = useState(false)
  const formik = useFormikContext<MilliplexFormValues>()
  const selectedKits = formik.values.selectedKits as SelectedKits
  const productDetails = Object.values(selectedKits).map((kit) => kit.data)

  const { isDealer, currentUser } = useCurrentUser()
  const { userSession } = useUserSession()
  const date = new Date()
  const timeZone = (date.getTimezoneOffset() / 60) * -1
  const isAuUser =
    currentUser?.__typename === 'LoggedInUser' &&
    currentUser.metadata?.country &&
    currentUser.metadata?.country === 'AU'
  const [dialogOpen, setDialogOpen] = React.useState(false)
  const [totalItem, setTotalItem] = useState<number>(0)
  const [confMilliplexAddToCart, { data, loading }] =
    useAddToCartMilliplexMutation()

  let isSAPNextEnabled: boolean
  let store = ''

  if (
    currentUser?.__typename === 'LoggedInUser' &&
    currentUser.metadata?.isSAPNextEnabled
  ) {
    isSAPNextEnabled = currentUser.metadata?.isSAPNextEnabled
  } else if (
    userSession.accessToken &&
    currentUser?.metadata?.isSAPNextEnabled
  ) {
    isSAPNextEnabled = currentUser?.metadata?.isSAPNextEnabled
  } else {
    isSAPNextEnabled = true
  }

  if (userSession.store) {
    store = userSession.store
  }
  const milliplexProductInput: MilliplexProductInput = {
    sapNextEnabled: isSAPNextEnabled,
    timeZone: timeZone.toString(),
    store: store,
  }

  const calculatePrice = () => {
    let totalPrice: any = 0
    const [product] = productDetails

    const hasInvalidPrice = productDetails.some(
      (prod) => !prod.price || prod.price === ''
    )

    if (hasInvalidPrice) {
      return '--'
    }

    productDetails.forEach((prod) => {
      if (prod.price && prod.quantity) {
        totalPrice = totalPrice + prod.quantity * parseFloat(prod.price)
      }
    })
    if (totalPrice !== 0) {
      return (
        <FormattedNumber
          style="currency"
          currencyDisplay="symbol"
          currency={product.currency}
          value={parseFloat(totalPrice)}
        />
      )
    }
    return '--'
  }

  const handleAddToCart = async () => {
    const cartData: any = []
    const configuratorEventProducts: ConfiguratorEventProduct[] = []
    if (productDetails && productDetails.length > 0) {
      productDetails.forEach((product, index) => {
        const obj = {
          panelId: product.milliplexKitDataFromApi?.panelId,
          panelName: product.milliplexKitDataFromApi?.panelName,
          assayType: product.milliplexKitDataFromApi?.assayType,
          customPremixedAvailable:
            product.milliplexKitDataFromApi?.customPremixedAvailable,
          requireSample: 'yes',
          species: product.milliplexKitDataFromApi?.species,
          researchAreas: formik.values?.researchAreas,
          type: 'Milliplex',
          listSampleType: product.milliplexKitDataFromApi?.listSampleType,
          customPremixSelection: product.customPremixing,
          selectedAnalytes: product.analytesInKit,
          analytes: product.milliplexKitDataFromApi?.analytes,
          quantity: product.quantity,
          material: '',
          spaceSaver: product?.spaceSaving,
          sampleType: product?.sampleType,
          id: index + 1,
          appendToMaterialNumber:
            product.milliplexKitDataFromApi?.appendToMaterialNumber,
        }
        cartData.push(obj)
        // Adding data for GA Event
        const productId = product.productNo?.toLowerCase() || ''
        configuratorEventProducts.push({
          id: productId,
          variant: productId,
          name:
            product.productName
              ?.toLowerCase()
              .replace(regexForStripEncodingUnicode, '')
              .replace(regexForStripHTML, '')
              .replace(regexForStripSymbols, '') || '',
          brand:
            product.milliplexKitDataFromApi?.brand
              ?.toLowerCase()
              .replace(regexForStripSymbols, '') || '',
          quantity: 1,
          price: priceFormatter(parseFloat(product?.price || '')),
          dimension91: 'standard',
        })
      })

      sendAddToCartConfiguratorEvent(
        configuratorEventProducts,
        productDetails[0].currency
      )

      await confMilliplexAddToCart({
        variables: {
          input: cartData,
          productInput: milliplexProductInput,
        },
        fetchPolicy: 'no-cache',
      })
    }
  }

  React.useEffect(() => {
    if (data?.confMilliplexAddToCart?.result === 'success') {
      setDialogOpen(true)
      setTotalItem(productDetails.length)
    }
  }, [data])

  return (
    <div className={classes.containerWrapper}>
      <Grid container className={classes.content}>
        <Grid item className={classes.heading}>
          <FormattedMessage id="SUBTOTAL" defaultMessage="Subtotal" />
          :&nbsp;
          {!isDealer && isAuUser ? (
            <FormattedMessage id="QUOTE_PRICE" defaultMessage="Quote Price" />
          ) : (
            <span>{calculatePrice()}</span>
          )}
        </Grid>
        <Divider className={classes.divider} />
        <Grid item className={classes.noteInfo}>
          {!isDealer && isAuUser ? (
            <p>
              <FormattedMessage
                id="CLICK_SEND_REQUEST_TO_DEALER"
                defaultMessage="Click Send Request to Dealer to get a price quotation and arrange an order by providing your contact information"
              />
            </p>
          ) : (
            <>
              <p className={classes.noteText}>
                <FormattedMessage id="CART_ITEM_NOTE" defaultMessage="Note" />
                :&nbsp;
                <FormattedMessage
                  id="CONF_PRICE_SUB_TOTAL"
                  defaultMessage="Price displayed is a subtotal amount."
                />
              </p>
              <p>
                -&nbsp;
                <FormattedMessage
                  id="CONF_APPLICABLE_FREIGHT_TAX_AND_FEES_DURING_CHECKOUT"
                  defaultMessage="Applicable freight, taxes & fees will be applied during checkout."
                />
              </p>
              <p>
                -&nbsp;
                <FormattedMessage
                  id="CONF_ORDER_ACKNOWLEDGEMENT_MESSAGE"
                  defaultMessage="You will receive an order acknowledgement once the order has been entered into our system."
                />
              </p>
              <p>
                -&nbsp;
                <FormattedMessage
                  id="CONF_SERVICE_FEE_FOR_EACH_KIT_MESSAGE"
                  defaultMessage="There is a service fee added for each kit where the custom premix option is selected."
                />
              </p>
            </>
          )}
        </Grid>
        <Grid item>
          {!isDealer && isAuUser ? (
            <Button
              variant="contained"
              color="secondary"
              size="medium"
              className={classes.addToCart}
              onClick={() => {
                setSendRequestToDealerModal(true)
              }}
            >
              <FormattedMessage
                id="SEND_REQUEST_TO_DEALER"
                defaultMessage="Send Request to Dealer"
              />
            </Button>
          ) : (
            <Button
              variant="contained"
              color="secondary"
              size="medium"
              className={classes.addToCart}
              onClick={handleAddToCart}
            >
              <FormattedMessage id="ADD_TO_CART" defaultMessage="Add to Cart" />
            </Button>
          )}
        </Grid>
        <AddToCartItems
          dialogOpen={dialogOpen}
          setDialogOpen={setDialogOpen}
          price={calculatePrice()}
          totalItem={totalItem}
          productType={ProductEnum.Milliplex}
        />
        <Grid item className={classes.shareDiv}>
          <ShareNewIcon
            color="primary"
            fontSize="small"
            className={classes.shareIcon}
            onClick={() => {
              setShareLinkClicked(true)
            }}
          />
          <span className={classes.shareText}>
            <Link
              className={classes.shareLink}
              onClick={() => {
                setShareLinkClicked(true)
              }}
            >
              <FormattedMessage id="Milliplex_Share" defaultMessage="Share" />
            </Link>
          </span>
        </Grid>
        {shareLinkClicked && (
          <Grid item>
            <ShareLinkDropDown setShareLinkClicked={setShareLinkClicked} />
          </Grid>
        )}
      </Grid>
      <QuoteRequestToDealerModal
        open={sendRequestToDealerModal}
        handleClose={() => {
          setSendRequestToDealerModal(false)
        }}
        selectedKits={selectedKits}
        species={formik.values.species}
        researchAreas={formik.values.researchAreas}
        milliplexFormik={formik}
      />
      {loading && <CircleLoader />}
    </div>
  )
}

export default SubtotalAndAddToCart
